/**
 * Ops Admin Shell — router, auth gate, persistent banner.
 *
 * Renders a distinct OPS ADMIN banner so staff never confuse this
 * surface with the customer-facing UI.
 */
import { Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../api';
import OrgList from './OrgList';
import OrgDetail from './OrgDetail';
import SystemDashboard from './SystemDashboard';
import StageDrift from './StageDrift';
import AuditBrowser from './AuditBrowser';
import OpsUsers from './OpsUsers';
import HelpTickets from './HelpTickets';
import SentencingMatches from './SentencingMatches';
import UsersSearch from './UsersSearch';
import BackgroundJobs from './BackgroundJobs';
import Errors from './Errors';

function OpsAdminBanner({ role }) {
  return (
    <div className="ops-admin-banner">
      <span className="ops-admin-banner-label">OPS ADMIN</span>
      {role && <span className="ops-admin-banner-role">{role.toUpperCase()}</span>}
    </div>
  );
}

function OpsAdminNav({ role }) {
  const isOwner = role === 'owner';
  return (
    <nav className="ops-admin-nav">
      <NavLink to="/ops_admin/orgs" className={({ isActive }) => isActive ? 'ops-nav-link active' : 'ops-nav-link'}>
        Orgs
      </NavLink>
      <NavLink to="/ops_admin/find_users" className={({ isActive }) => isActive ? 'ops-nav-link active' : 'ops-nav-link'}>
        Find Users
      </NavLink>
      <NavLink to="/ops_admin/system" className={({ isActive }) => isActive ? 'ops-nav-link active' : 'ops-nav-link'}>
        System
      </NavLink>
      <NavLink to="/ops_admin/jobs" className={({ isActive }) => isActive ? 'ops-nav-link active' : 'ops-nav-link'}>
        Jobs
      </NavLink>
      <NavLink to="/ops_admin/errors" className={({ isActive }) => isActive ? 'ops-nav-link active' : 'ops-nav-link'}>
        Errors
      </NavLink>
      <NavLink to="/ops_admin/drift" className={({ isActive }) => isActive ? 'ops-nav-link active' : 'ops-nav-link'}>
        Drift
      </NavLink>
      <NavLink to="/ops_admin/audit" className={({ isActive }) => isActive ? 'ops-nav-link active' : 'ops-nav-link'}>
        Audit
      </NavLink>
      <NavLink to="/ops_admin/help_tickets" className={({ isActive }) => isActive ? 'ops-nav-link active' : 'ops-nav-link'}>
        Help tickets
      </NavLink>
      <NavLink to="/ops_admin/sentencing_matches" className={({ isActive }) => isActive ? 'ops-nav-link active' : 'ops-nav-link'}>
        Sentencing matches
      </NavLink>
      {isOwner && (
        <NavLink to="/ops_admin/users" className={({ isActive }) => isActive ? 'ops-nav-link active' : 'ops-nav-link'}>
          Users
        </NavLink>
      )}
      <a href="/cases" className="ops-nav-link ops-nav-exit">
        &larr; Back to App
      </a>
    </nav>
  );
}

export default function OpsAdminShell() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [opsRole, setOpsRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!user || !token) {
      navigate('/login');
      return;
    }

    // Probe ops access by calling a read endpoint
    api.get('/ops_admin/orgs?limit=1')
      .then(data => {
        // Determine role from a dedicated endpoint or extract from response header
        return api.get('/ops_admin/ops_users').then(u => {
          const me = u.ops_users?.find(x => x.email === user.email);
          setOpsRole(me?.role ?? 'readonly');
        }).catch(() => {
          // If ops_users returns 403, we have non-owner access
          setOpsRole('support');
        });
      })
      .catch(err => {
        if (err.status === 403 || err.message?.includes('403')) {
          setDenied(true);
        }
      })
      .finally(() => setLoading(false));
  }, [user, token, navigate]);

  if (loading) {
    return (
      <div className="ops-admin-loading">
        <OpsAdminBanner role={null} />
        <p>Verifying ops access...</p>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="ops-admin-denied">
        <OpsAdminBanner role={null} />
        <p>You do not have ops admin access. Contact an owner to request access.</p>
        <a href="/cases" className="btn btn-primary">Back to App</a>
      </div>
    );
  }

  return (
    <div className="ops-admin-shell">
      <OpsAdminBanner role={opsRole} />
      <OpsAdminNav role={opsRole} />
      <div className="ops-admin-content">
        <Routes>
          <Route index element={<Navigate to="orgs" replace />} />
          <Route path="orgs" element={<OrgList opsRole={opsRole} />} />
          <Route path="find_users" element={<UsersSearch />} />
          <Route path="orgs/:orgId/*" element={<OrgDetail opsRole={opsRole} />} />
          <Route path="system" element={<SystemDashboard opsRole={opsRole} />} />
          <Route path="jobs" element={<BackgroundJobs />} />
          <Route path="errors" element={<Errors />} />
          <Route path="drift" element={<StageDrift />} />
          <Route path="audit" element={<AuditBrowser />} />
          <Route path="help_tickets" element={<HelpTickets opsRole={opsRole} />} />
          <Route path="sentencing_matches" element={<SentencingMatches opsRole={opsRole} />} />
          <Route path="users" element={<OpsUsers opsRole={opsRole} />} />
          <Route path="*" element={<Navigate to="orgs" replace />} />
        </Routes>
      </div>
    </div>
  );
}
